<template>
    <div
        id="livesearch"
        class="livesearch animated fadeIn"
        @keyup.esc="blur"
        @keyup.down="incrementSelectedResultIndex"
        @keyup.up="decrementSelectedResultIndex"
    >
        <div class="livesearch__input kgr-inputIcon">
            <input
                v-model="q"
                type="text"
                class="kgr-inputIcon__input kgr-input kgr-input--small"
                :placeholder="placeholder"
                @keyup="search"
                @focus="focus"
                @click="focus"
            />
            <span class="kgr-inputIcon__icon">
                <i
                    v-show="q.length > 0"
                    class="icon-close"
                    @click="q = ''"
                ></i>
                <i
                    v-show="q.length === 0"
                    class="icon-search"
                ></i>
            </span>
        </div>
        <div
            v-show="showResultlist"
            class="results searchresults kgr-region kgr-region--default kgr-region--depth kgr-region--rounded kgr-region--noPadding"
        >
            <div
                v-if="searching"
                class="searching"
            >
                <i class="icon-spinner icon-pulse"></i>
            </div>
            <ul v-if="!searching">
                <li
                    v-for="(result, index) in results"
                    :key="result.identity"
                    class="list-item result-item kgr-region__unpadHorizontally kgr-region"
                    style="text-align: left"
                    :class="{ 'last': index === lastResultIndex, 'active': index === selectedResultIndex }"
                    @click="isDogSearch ? showDogAndMemberSelectDialog(result) : showMemberSelectDialog(result)"
                    @mouseover="selectResult(index)"
                >
                    <div>
                        <div class="avatar default">
                            <img :src="result.image" />
                        </div>
                    </div>
                    <div>
                        <h4>
                            <kgr-highlight
                                :value="result.title"
                                :search-phrase="q"
                            />
                        </h4>
                        <div v-if="result.category === 'member'">
                            <p class="type">
                                <kgr-member-contract-state-icon :state="getMemberContractState(result.contractState)" />
                                {{ result.type }}
                                <template v-if="result.membershipNumber">
                                    (<kgr-highlight
                                        :value="result.membershipNumber"
                                        :search-phrase="q"
                                    />)
                                </template>
                            </p>
                        </div>
                        <div v-else-if="result.category === 'child'">
                            <p class="type">
                                <kgr-child-status-icon
                                    :status="result.contractStatus"
                                    small
                                    style="float: left"
                                />
                                <span>&nbsp;{{ result.type }} ({{ $t('{years} Jahre', {years: result.age}) }})</span>
                            </p>
                        </div>
                        <p
                            v-else
                            class="type"
                            v-text="result.type"
                        ></p>
                        <p
                            v-if="result.institutionName"
                            v-text="result.institutionName"
                        ></p>
                        <p v-if="result.match">
                            {{ result.match.key }}:
                            <kgr-highlight
                                :value="result.match.value"
                                :search-phrase="q"
                            />
                        </p>
                    </div>
                </li>
                <li
                    v-show="showNoResultsItem"
                    class="list-item no-results"
                >
                    <h4>{{ $t('Es wurden keine Ergebnisse gefunden.') }}</h4>
                </li>
            </ul>
        </div>
        <kgr-member-select-dialog
            v-if="showMemberSelect"
            :offering="offering"
            :member-id="memberId"
            :member-name="memberName"
            :show="showMemberSelect"
            :loading="loadingAddParticipant"
            @onClose="closeMemberSelectDialog()"
            @selectMember="selectMember($event)"
            @update:selectedSalesListOfferingItems="$emit('update:selectedSalesListOfferingItems', $event)"
        />
        <kgr-dog-member-select-dialog
            v-if="showDogAndMemberSelect"
            :offering="offering"
            :show="showDogAndMemberSelect"
            :dog-id="dogId"
            :loading="loadingAddParticipant"
            @onClose="closeDogAndMemberSelectDialog()"
            @selectDogAndMember="selectDogAndMember($event)"
            @update:selectedSalesListOfferingItems="$emit('update:selectedSalesListOfferingItems', $event)"
        />
    </div>
</template>

<script>
import ChildStatusIcon from '../child/contract/ChildStatusIcon.vue';
import Highlight from '../common/Highlight.vue';
import KgrMemberContractStateIcon from '../members/MemberContractStateIcon.vue';
import MemberContractState from '../../js/dto/MemberContractState.js';
import Offering from '../../js/dto/offering/Offering.js'
import OfferingDogMemberSelectDialog from './OfferingDogMemberSelectDialog.vue';
import OfferingMemberSelectDialog from './OfferingMemberSelectDialog.vue'
import axios from 'axios';

const MIN_SEARCH_PHRASE_LENGTH = 3;

export default {
    name: 'OfferingLiveSearchResultPreview',
    components: {
        'kgr-highlight': Highlight,
        'kgr-child-status-icon': ChildStatusIcon,
        'kgr-dog-member-select-dialog': OfferingDogMemberSelectDialog,
        'kgr-member-select-dialog': OfferingMemberSelectDialog,
        KgrMemberContractStateIcon,
    },
    props: {
        clubId: {
            type: Number,
            required: true,
        },
        offering: {
            type: Offering,
            required: true,
        },
        withCustomPlaceholder: {
            type: Boolean,
            required: false,
            default: false,
        },
        customPlaceholderString: {
            type: String,
            required: false,
            default: null,
        },
        isDogSearch: {
            type: Boolean,
            required: false,
            default: false,
        },
        loadingAddParticipant: {
            type: Boolean,
            required: false,
            default: false,
        }
    },
    data() {
        return {
            q: '',
            queryOfLastResult: '',
            results: [],
            hideResults: false,
            searchTimer: null,
            selectedResultIndex: null,
            showMemberSelect: false,
            showDogAndMemberSelect: false,
            memberId: null,
            memberName: null,
            dogId: null,
            selectedClubExams: [],
        }
    },
    computed: {
        showMoreResultsItem: function () {
            return this.results.length > 8;
        },
        showNoResultsItem: function () {
            return this.q.length > 0 && this.results.length === 0 && !this.searching;
        },
        searching: function () {
            return this.q.trim().toLowerCase() !== this.queryOfLastResult.trim().toLowerCase();
        },
        showResultlist: function () {
            return this.q.trim().length >= MIN_SEARCH_PHRASE_LENGTH && !this.hideResults;
        },
        numberOfRows: function () {
            let numberOfRows = Math.min(8, this.results.length);
            if (this.showMoreResultsItem) {
                numberOfRows++;
            }

            return numberOfRows;
        },
        lastResultIndex: function () {
            return Math.min(8, this.results.length) - 1;
        },
        placeholder() {
            if (this.withCustomPlaceholder) {
                if (this.customPlaceholderString === null) {
                    return this.$t('Suchbegriff');
                } else {
                    return this.customPlaceholderString;
                }
            } else {
                return this.$t('Mitgliedersuche');
            }
        },
    },
    methods: {
        search: function (event) {
            if (['Escape', 'ArrowDown', 'ArrowUp', 'Enter'].includes(event.key)) {
                return;
            }
            if(this.q.trim().length < MIN_SEARCH_PHRASE_LENGTH){
                return;
            }

            this.hideResults = false;
            this.selectedResultIndex = null;
            clearTimeout(this.searchTimer);
            if (this.q.trim() === '') {
                this.results = [];
                return;
            }

            const strokesPerMinute = 200;
            this.searchTimer = setTimeout(function () {
                this.sendSearchRequest();
            }.bind(this), 1000 / (strokesPerMinute / 60));
        },
        sendSearchRequest: function () {
            const memberSearchRoute = `/backend/clubs/${this.clubId}/search/members?q=${this.q}`;
            const dogSearchRoute = `/backend/clubs/${this.clubId}/search/dogs?q=${this.q}`;
            const request = axios.create({
                baseURL: window.location.origin
            });

            request
                .get(this.isDogSearch ? dogSearchRoute : memberSearchRoute)
                .then(response => {
                    if (this.q !== response.data.q) {
                        return;
                    }
                    this.queryOfLastResult = response.data.q.toLowerCase();
                    this.results = response.data.results;
                });
        },
        selectMember: function (params) {
            params.id = this.memberId;
            this.$emit('memberSelected', params);
        },
        selectDogAndMember(params) {
            this.showDogAndMemberSelect = false;
            this.$emit('dogAndMemberSelected', params);
        },
        showMemberSelectDialog(member) {
            this.memberId = member.id;
            this.memberName = member.title;
            this.showMemberSelect = true;
            this.emitOpenModal();
        },
        closeMemberSelectDialog() {
            this.showMemberSelect = false;
            this.emitCloseModal();
        },
        showDogAndMemberSelectDialog(dog) {
            this.dogId = dog.identity;
            this.showDogAndMemberSelect = true;
            this.emitOpenModal();
        },
        closeDogAndMemberSelectDialog() {
            this.showDogAndMemberSelect = false;
            this.emitCloseModal();
        },
        emitOpenModal() {
            this.$emit('openModal');
        },
        emitCloseModal() {
            this.$emit('closeModal');
        },
        incrementSelectedResultIndex: function () {
            if (this.results.length === 0) {
                return;
            }
            if (this.selectedResultIndex === null) {
                this.selectedResultIndex = 0;
            } else {
                this.selectedResultIndex++;
            }
            if (this.selectedResultIndex > this.numberOfRows - 1) {
                this.selectedResultIndex = 0;
            }
        },
        decrementSelectedResultIndex: function () {
            if (this.results.length === 0) {
                return;
            }
            if (this.selectedResultIndex === null) {
                this.selectedResultIndex = this.numberOfRows - 1;
            } else {
                this.selectedResultIndex--;
            }
            if (this.selectedResultIndex < 0) {
                this.selectedResultIndex = this.numberOfRows - 1;
            }
        },
        blur: function () {
            this.hideResults = true;
        },
        focus: function () {
            this.hideResults = false;
        },
        selectResult: function (index) {
            this.selectedResultIndex = index;
        },
        getMemberContractState(data) {
            return new MemberContractState(data.status, data.contractType);
        }
    }
}
</script>

<style rel="stylesheet/scss" lang="scss" scoped>
</style>
